import React, { useEffect, useState } from "react";

import { Turnstile } from "@marsidev/react-turnstile";
import { isMessageKey } from "common/i18n/types";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { Header } from "../components/layout/header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { config } from "../config";
import { useAuth } from "../contexts/auth-context";
import { useIsDark } from "../contexts/theme-context";
import { useTranslations } from "../hooks/use-translations";
import { apiClient } from "../lib/api";

export const RegisterPage = (): React.JSX.Element => {
  const isDark = useIsDark();
  const [firstName, setFirstName] = useState(``);
  const [lastName, setLastName] = useState(``);
  const [email, setEmail] = useState(``);
  const [password, setPassword] = useState(``);
  const [confirmPassword, setConfirmPassword] = useState(``);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(``);
  const [turnstileToken, setTurnstileToken] = useState(``);
  const turnstileEnabled = config.features.turnstileProtection;
  const csrfEnabled = config.features.csrfProtection;
  const { turnstileSiteKey } = config;

  const { register, isAuthenticated } = useAuth();
  const { translations } = useTranslations();
  const navigate = useNavigate();

  useEffect(() => {
    if (csrfEnabled) {
      const loadToken = async () => {
        const data = await apiClient.fetchCsrfToken();

        apiClient.setCsrfToken(data.csrfToken);
      };

      loadToken();
    }
  }, [csrfEnabled]);

  if (isAuthenticated) {
    return <Navigate replace to="/account/data" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(``);

    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setError(translations.common.pleaseFillAllFields);
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(translations.common.passwordsDoNotMatch);
      setIsLoading(false);
      return;
    }

    try {
      await register(email, password, firstName, lastName, turnstileToken);

      const search = new URLSearchParams();

      search.set(`email`, email);

      navigate(`/account-created?${search.toString()}`);
    } catch (err) {
      if (err instanceof Error) {
        const errorMessage = err.message;

        if (
          errorMessage === `networkError` ||
          errorMessage.includes(`network`) ||
          errorMessage.includes(`unable to connect`) ||
          errorMessage.includes(`fetch`)
        ) {
          setError(translations.common.networkError);
        } else if (
          errorMessage === `unexpectedError` ||
          errorMessage.includes(`server error`) ||
          errorMessage.includes(`internal server error`)
        ) {
          setError(translations.common.serverError);
        } else if (isMessageKey(errorMessage)) {
          setError(translations.auth[errorMessage]);
        } else {
          setError(errorMessage);
        }
      } else {
        setError(translations.register.failure);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const wrapperBg = isDark ? `bg-gray-900` : `bg-white`;

  return (
    <div className={`min-h-screen flex flex-col ${wrapperBg}`}>
      <Header />
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card data-testid="register-card">
            <CardHeader>
              <CardTitle className="text-center">{translations.register.title}</CardTitle>
              <CardDescription className="text-center">{translations.register.description}</CardDescription>
            </CardHeader>
            {error ? (
              <CardContent>
                <div
                  className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md"
                  data-testid="register-error"
                >
                  {error}
                </div>
              </CardContent>
            ) : null}
            <CardContent>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    required
                    data-testid="register-first-name"
                    disabled={isLoading}
                    name="firstName"
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={translations.common.firstNamePlaceholder}
                    type="text"
                    value={firstName}
                  />
                  <Input
                    required
                    data-testid="register-last-name"
                    disabled={isLoading}
                    name="lastName"
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={translations.common.lastNamePlaceholder}
                    type="text"
                    value={lastName}
                  />
                </div>

                <div>
                  <Input
                    required
                    data-testid="register-email"
                    disabled={isLoading}
                    name="email"
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={translations.common.emailPlaceholder}
                    type="email"
                    value={email}
                  />
                </div>

                <div>
                  <Input
                    required
                    data-testid="register-password"
                    disabled={isLoading}
                    name="password"
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={translations.common.passwordPlaceholder}
                    type="password"
                    value={password}
                  />
                </div>

                <div>
                  <Input
                    required
                    data-testid="register-confirm-password"
                    disabled={isLoading}
                    name="confirmPassword"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={translations.common.confirmPasswordPlaceholder}
                    type="password"
                    value={confirmPassword}
                  />
                </div>

                {turnstileEnabled ? <Turnstile onSuccess={setTurnstileToken} siteKey={turnstileSiteKey} /> : null}

                <Button
                  className="w-full"
                  data-testid="register-submit"
                  disabled={isLoading || (turnstileEnabled && !turnstileToken)}
                  isLoading={isLoading}
                  loadingText={translations.common.loading}
                  type="submit"
                >
                  {translations.common.createAccount}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {translations.register.alreadyHaveAccount}
                  {` `}
                  <Link
                    className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                    to="/login"
                  >
                    {translations.common.signIn}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <footer className="py-3">
        <div className="text-center text-xs text-gray-500 dark:text-gray-400">
          <span>{translations.common.supportContact} </span>
          <a className="underline hover:no-underline" href={`mailto:${config.supportEmail}`}>
            {config.supportEmail}
          </a>
        </div>
      </footer>
    </div>
  );
};
